/** Display helpers. Probability is 0–1 internally; percent once here. */

export function formatMoney(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return `$${Number(n).toFixed(2)}`;
}

export function formatStrikeUsd(k) {
  if (k == null || Number.isNaN(Number(k))) return "";
  const n = Number(k);
  if (Math.abs(n - Math.round(n)) < 0.001) return `$${Math.round(n)}`;
  return `$${n}`;
}

export function formatStrikeLabel(e) {
  if (!e) return "—";
  if (e.strikes_label) return e.strikes_label;
  const short = formatStrikeUsd(e.short_strike);
  if (!short) return "—";
  const long = formatStrikeUsd(e.long_strike);
  const strat = String(e.strategy || "").toUpperCase();
  if (long && ["BPS", "BCS", "DCS"].includes(strat) && Math.abs(Number(e.long_strike) - Number(e.short_strike)) >= 0.01) {
    return `${short}/${long}`;
  }
  return short;
}

const MINUS = "\u2212";
const EMDASH = "\u2014";

export function formatDeltaToken(value, source, label, extras) {
  const src = String(source || "").trim().toLowerCase();
  const extra = extras && typeof extras === "object" ? extras : {};
  const fresh = String(extra.freshness || extra.delta_freshness || "").trim().toLowerCase() || src;
  const x = value == null || Number.isNaN(Number(value)) ? null : Number(value);
  if (fresh === "prior_session_stale" || src === "prior_session_stale") return `Δ prior-session stale ${EMDASH} refresh required`;
  if (fresh === "stale" || src === "stale") return `Δ stale ${EMDASH} refresh required`;
  if (x == null) return "Δ unavailable";
  if (src === "unavailable" || src === "invalid" || fresh === "unavailable") return "Δ unavailable";
  if (!src && !fresh && x === 0) return "Δ unavailable";
  const sign = x < 0 ? MINUS : (x > 0 ? "+" : "");
  const modelled = src === "modelled" || fresh === "modelled" || (label && String(label).toLowerCase().includes("modelled"));
  if (modelled) return `Δ ~${sign}${Math.abs(x).toFixed(2)} modelled`;
  let token = `Δ ${sign}${Math.abs(x).toFixed(2)}`;
  const delay = String(extra.delay_class || "").trim().toLowerCase();
  const eod = fresh === "eod" || delay === "eod" || delay === "close" || delay === "end_of_day";
  if (eod) {
    const asOf = extra.as_of ? String(extra.as_of).slice(0, 10) : "";
    token += " EOD";
    if (asOf.length === 10) token += ` · ${asOf}`;
  }
  return token;
}

function deltaExtras(e, prefix) {
  if (prefix === "short") {
    return {
      freshness: e.short_delta_freshness || e.delta_freshness,
      delay_class: e.delay_class,
      as_of: e.as_of,
    };
  }
  if (prefix === "long") {
    return {
      freshness: e.long_delta_freshness || e.delta_freshness,
      delay_class: e.delay_class,
      as_of: e.as_of,
    };
  }
  return {
    freshness: e.delta_freshness || e.short_delta_freshness,
    delay_class: e.delay_class,
    as_of: e.as_of,
  };
}

export function formatSelectedLegLabel(e) {
  if (!e) return "—";
  if (e.selected_leg_label) return e.selected_leg_label;
  const strat = String(e.strategy || "").toUpperCase();
  const short = formatStrikeUsd(e.short_strike);
  const long = formatStrikeUsd(e.long_strike);
  const tgt = e.target_delta == null || Number.isNaN(Number(e.target_delta))
    ? null
    : Math.abs(Number(e.target_delta)).toFixed(2);
  if (strat === "BPS") {
    if (!short) return "—";
    const shortD = formatDeltaToken(e.short_delta ?? e.delta, e.short_delta_source || e.delta_source, e.short_delta_label || e.delta_label, deltaExtras(e, "short"));
    const longD = formatDeltaToken(e.long_delta, e.long_delta_source, e.long_delta_label, deltaExtras(e, "long"));
    const longPart = long ? `Long ${long}P ${longD}` : `Long ${longD}`;
    return `Short ${short}P ${shortD} / ${longPart}`;
  }
  if (strat === "BCS") {
    const longD = formatDeltaToken(e.long_delta, e.long_delta_source, e.long_delta_label, deltaExtras(e, "long"));
    const shortD = formatDeltaToken(e.short_delta ?? e.delta, e.short_delta_source || e.delta_source, e.short_delta_label || e.delta_label, deltaExtras(e, "short"));
    const longPart = long ? `Long ${long}C ${longD}` : `Long ${longD}`;
    const shortPart = short ? `Short ${short}C ${shortD}` : `Short ${shortD}`;
    return `${longPart} / ${shortPart}`;
  }
  if (!short) return formatStrikeLabel(e);
  const ot = String(e.option_type || "").toLowerCase();
  const kind = ot.startsWith("c") || strat === "CC" ? "Call" : "Put";
  const dtok = formatDeltaToken(e.short_delta ?? e.delta, e.delta_source || e.short_delta_source, e.delta_label || e.short_delta_label, deltaExtras(e));
  let out = `${short} ${kind} · ${dtok}`;
  if (tgt) out += ` · target |Δ| ${tgt}`;
  return out;
}

export function formatProbPct(p) {
  if (p == null || Number.isNaN(Number(p))) return "—";
  let x = Number(p);
  if (x > 1.0001) x = x / 100;
  if (x < 0 || x > 1) return "—";
  return `${Math.round(x * 100)}%`;
}

export function isValidProb(p) {
  if (p == null || Number.isNaN(Number(p))) return false;
  let x = Number(p);
  if (x > 1.0001) x = x / 100;
  return x >= 0 && x <= 1;
}

export const PREP_PAIR_LIMIT = 8;

export function pairKey(e) {
  return `${String(e?.ticker || "").toUpperCase()}|${String(e?.expiry || "").slice(0, 10)}`;
}

export function isRefreshEligible(e) {
  const ticker = String(e?.ticker || "").trim();
  const expiry = String(e?.expiry || "").slice(0, 10);
  if (!ticker || expiry.length < 10) return false;
  if (e?.refresh_eligible === false) return false;
  return true;
}

export function tradeBlockLines(e) {
  const raw = tradeBlockReason(e);
  if (!raw) return [];
  return String(raw)
    .split(/\r?\n/)
    .map((s) => s.replace(/^\s*-\s*/, "").trim())
    .filter((s) => s && !/^trade selection unavailable:?$/i.test(s));
}

export function tradeBlockReason(e) {
  if (e?.trade_block_reason) return e.trade_block_reason;
  if (!isValidProb(e?.pop)) {
    return "Trade selection unavailable:\n- Invalid probability of profit";
  }
  if (e?.selectable === false) {
    return e.option_freshness?.banner
      || e.option_freshness?.user_label
      || e.freshness_label
      || "Trade selection unavailable:\n- Not eligible for trade selection";
  }
  return null;
}

export function formatQuoteLine(e) {
  const parts = [];
  const bid = e?.short_bid ?? e?.bid;
  const ask = e?.short_ask ?? e?.ask;
  const last = e?.short_last ?? e?.last_price;
  if (bid != null && bid !== "") parts.push(`bid ${Number(bid).toFixed(2)}`);
  if (ask != null && ask !== "") parts.push(`ask ${Number(ask).toFixed(2)}`);
  if (last != null && last !== "") parts.push(`last ${Number(last).toFixed(2)}`);
  if (e?.long_bid != null || e?.long_ask != null) {
    const lb = e.long_bid != null ? Number(e.long_bid).toFixed(2) : "—";
    const la = e.long_ask != null ? Number(e.long_ask).toFixed(2) : "—";
    parts.push(`long ${lb}/${la}`);
  }
  const label = e?.pricing_display_label || e?.pricing_method;
  if (e?.quote_complete && e?.premium != null) {
    parts.push(`${label} ${Number(e.premium).toFixed(2)}`);
  } else if (label) {
    parts.push(String(label));
  } else if (e?.quote_complete === false) {
    parts.push("Partial quote data");
  }
  if (e?.contract_id) parts.push(e.contract_id);
  return parts.join(" · ");
}

export function formatRefreshOutcome(raw) {
  const s = String(raw || "").trim().toLowerCase();
  const map = {
    queued: "queued",
    claimed: "queued",
    running: "running",
    refreshed: "refreshed",
    done: "refreshed",
    completed: "refreshed",
    timestamp_unverified: "timestamp unverifiable",
    refreshed_timestamp_unverified: "timestamp unverifiable",
    unchanged: "unchanged — no newer data",
    unchanged_no_newer_data: "unchanged — no newer data",
    failed: "failed",
    retry: "retry later",
    retry_later: "retry later",
    stale_provider_data_rejected: "stale provider data rejected",
    provider_unavailable: "provider unavailable",
    invalid_response: "invalid response",
    rate_limited: "rate limited",
  };
  return map[s] || (raw ? String(raw) : null);
}

export function formatRefreshReport(report, fallbackOutcome) {
  if (!report && !fallbackOutcome) return null;
  const r = report && typeof report === "object" ? report : {};
  const parts = [];
  if (r.ticker) parts.push(String(r.ticker).toUpperCase());
  if (r.expiry) parts.push(String(r.expiry).slice(0, 10));
  const outcome = formatRefreshOutcome(r.outcome || fallbackOutcome);
  if (outcome) parts.push(outcome);
  if (Array.isArray(r.providers_tried) && r.providers_tried.length) {
    parts.push(`providers ${r.providers_tried.join(", ")}`);
  }
  if (r.option_quote_as_of) parts.push(`quote ${r.option_quote_as_of}`);
  if (r.option_snapshot_fetched_at) parts.push(`fetched ${r.option_snapshot_fetched_at}`);
  if (r.quote_timestamp_quality) parts.push(`quality ${r.quote_timestamp_quality}`);
  if (r.retry_after_sec != null) parts.push(`retry ${r.retry_after_sec}s`);
  if (r.freshness_label) parts.push(r.freshness_label);
  return parts.join(" · ") || outcome;
}

export function refreshOutcomeFor(e, preparation, prepareAck, lastReports) {
  const tk = String(e?.ticker || "").toUpperCase();
  const exp = String(e?.expiry || "").slice(0, 10);
  const key = `${tk}|${exp}`;
  const report = lastReports?.[key] || lastReports?.[tk];
  if (report) return formatRefreshReport(report);
  const items = [...(prepareAck?.items || []), ...(preparation?.items || [])];
  let hit = null;
  for (const i of items) {
    if (String(i?.ticker || "").toUpperCase() === tk && String(i?.expiry || "").slice(0, 10) === exp) {
      hit = i;
    }
  }
  if (hit?.report) return formatRefreshReport(hit.report, hit.outcome || hit.status);
  const raw = hit?.outcome || hit?.status || e?.refresh_job_status;
  return formatRefreshOutcome(raw);
}

export function inputQualityLabel(e) {
  return e?.input_quality_label
    || e?.row_status?.input_quality_label
    || e?.wing_scenarios?.input_quality_label
    || null;
}

export function formatDdMmYy(raw) {
  const s = String(raw || "").slice(0, 10);
  if (s.length < 10) return "";
  const [y, m, d] = s.split("-");
  if (!y || !m || !d) return "";
  return `${d}/${m}/${String(y).slice(-2)}`;
}

/** Display-only severity. Does not change stored status, pricing, or freshness. */
export function statusSeverity(text, extra = {}) {
  const s = String(text || "").toLowerCase();
  const st = String(extra.status || extra.option_freshness_status || "").toLowerCase();
  const quoteIncomplete = extra.quote_complete === false;
  if (
    quoteIncomplete
    || st === "timestamp_unverified"
    || /pricing unavailable|timestamp unverif|partial quote/.test(s)
  ) {
    return "blocking";
  }
  if (
    st === "prior_session_stale"
    || st === "stale"
    || /intraday stale|last-price|refresh required|prior-session stale|indicative/.test(s)
  ) {
    return "caution";
  }
  return "info";
}

export function compactStatusLabel(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  if (/timestamp unverif/i.test(s)) return "Timestamp unverifiable";
  if (/pricing unavailable/i.test(s)) return "Pricing unavailable";
  if (/partial quote/i.test(s)) return "Partial quote data";
  if (/intraday stale/i.test(s)) return "Intraday stale";
  if (/prior-session stale/i.test(s)) return "Prior-session stale";
  if (/last-price/i.test(s)) return "Last-price estimate";
  if (/modelled estimate/i.test(s)) return "Modelled estimate";
  if (/mid estimate/i.test(s)) return "Mid estimate";
  if (/natural market/i.test(s)) return "Natural market estimate";
  if (/end-of-day|end of day/i.test(s)) {
    const date = s.match(/\d{4}-\d{2}-\d{2}/);
    return date ? `End-of-day · ${date[0]}` : "End-of-day";
  }
  if (/^delayed/i.test(s)) return s.length > 36 ? "Delayed" : s;
  if (/pre-market/i.test(s)) return s.length > 40 ? "Pre-market snapshot" : s;
  return s.length > 36 ? `${s.slice(0, 34)}…` : s;
}

export function compactDeltaDisplay(e) {
  const x = e?.short_delta ?? e?.delta;
  if (x == null || Number.isNaN(Number(x))) return "—";
  const n = Number(x);
  const sign = n < 0 ? "\u2212" : (n > 0 ? "+" : "");
  return `${sign}${Math.abs(n).toFixed(2)}`;
}

export function primaryRowStatus(e) {
  if (!e) return null;
  const fresh = e.freshness_label || e.option_freshness?.user_label || "";
  const st = e.option_freshness?.status || "";
  const quoteLabel = e.pricing_display_label || e.pricing_method || "";
  const extra = {
    status: st,
    quote_complete: e.quote_complete,
    selectable: e.selectable,
  };
  if (e.quote_complete === false) {
    const text = (e.missing_quote_fields || []).length ? "Partial quote data" : "Pricing unavailable";
    return { tier: "blocking", text, full: quoteLabel || text };
  }
  if (st === "timestamp_unverified" || /timestamp unverif/i.test(fresh) || /timestamp unverif/i.test(quoteLabel)) {
    const full = fresh || "Timestamp unverifiable";
    return { tier: "blocking", text: compactStatusLabel(full), full };
  }
  if (st === "prior_session_stale" || st === "stale" || /stale|refresh required/i.test(fresh)) {
    const full = fresh || "Intraday stale";
    return { tier: "caution", text: compactStatusLabel(full), full };
  }
  if (/last-price/i.test(quoteLabel) || /last-price/i.test(fresh)) {
    const full = quoteLabel || fresh || "Last-price estimate";
    return { tier: "caution", text: compactStatusLabel(full), full };
  }
  if (fresh) {
    return { tier: statusSeverity(fresh, extra), text: compactStatusLabel(fresh), full: fresh };
  }
  if (quoteLabel) {
    return { tier: statusSeverity(quoteLabel, extra), text: compactStatusLabel(quoteLabel), full: quoteLabel };
  }
  const quality = inputQualityLabel(e);
  if (quality) {
    return { tier: statusSeverity(quality, extra), text: compactStatusLabel(quality), full: quality };
  }
  const lines = tradeBlockLines(e);
  if (lines.length) {
    return { tier: "blocking", text: compactStatusLabel(lines[0]), full: lines.join(" · ") };
  }
  return null;
}

export function workbenchOptionStatus(optionReadiness = {}, freshness = {}) {
  const tradeOk = Boolean(
    optionReadiness.trade_selection_eligible
    && !optionReadiness.freshness_blocks
    && !freshness.blocks_selection,
  );
  const primary = primaryRowStatus({
    freshness_label: freshness.user_label,
    option_freshness: freshness,
    quote_complete: optionReadiness.quote_complete,
    selectable: tradeOk,
    pricing_display_label: freshness.pricing_method || optionReadiness.pricing_method,
  });
  if (primary) return primary;
  if (tradeOk) {
    return { tier: "info", text: "Trade selection eligible", full: optionReadiness.summary || "" };
  }
  if (optionReadiness.quote_complete === false) {
    return { tier: "blocking", text: "Quote incomplete", full: optionReadiness.summary || "Quote incomplete" };
  }
  return { tier: "blocking", text: "Trade selection blocked", full: optionReadiness.summary || "Trade selection blocked" };
}

export function rowStatusDetailLines(e, extras = {}) {
  if (!e) return [];
  const out = [];
  const seen = new Set();
  const push = (line) => {
    const s = String(line || "").trim();
    if (!s || seen.has(s)) return;
    seen.add(s);
    out.push(s);
  };
  const fresh = e.freshness_label || e.option_freshness?.user_label;
  if (fresh) push(fresh);
  if (e.option_freshness?.banner) push(e.option_freshness.banner);
  tradeBlockLines(e).forEach((line) => push(line));
  const quote = formatQuoteLine(e);
  if (quote) push(quote);
  const quality = inputQualityLabel(e);
  if (quality) push(quality);
  if (e.pricing_display_label || e.pricing_method) {
    push(e.pricing_display_label || e.pricing_method);
  }
  if (e.as_of) push(`As of ${e.as_of}`);
  const fetched = e.option_freshness?.fetched_at_et
    || e.option_freshness?.option_snapshot_fetched_at
    || e.option_freshness?.fetched_at;
  if (fetched) push(`Fetched ${fetched}`);
  if (e.option_freshness?.provider) push(`Provider ${e.option_freshness.provider}`);
  if (e.option_freshness?.option_quote_as_of_et || e.option_freshness?.option_quote_as_of) {
    push(`Quote ${e.option_freshness.option_quote_as_of_et || e.option_freshness.option_quote_as_of}`);
  }
  if (extras.jobOutcome) push(extras.jobOutcome);
  const leg = e.selected_leg_label || formatSelectedLegLabel(e);
  if (leg) push(leg);
  return out;
}
